import React from 'react';
import { Link as LinkR } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import config from '../../aws-exports';

const {
  aws_user_files_s3_bucket_region: region,
  aws_user_files_s3_bucket: bucket,
} = config;

/* same working URL pattern the Discover page uses for the storefront photos */
const S3_PREFIX = `https://${bucket}.s3.${region}.amazonaws.com/public//public/us-east-1%3A6674e6cd-0af1-4444-abae-58974017d944/public/`;
const photoMain = `${S3_PREFIX}StoreFron6.jpeg`;
const photoTop = `${S3_PREFIX}StoreFron4.jpeg`;
const photoBottom = `${S3_PREFIX}StoreFront2.jpeg`;

const scrollToMarkets = () => {
  const el = document.getElementById('markets-list');
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const features = [
  { emoji: '🥬', title: 'Farm-fresh daily', text: 'Produce hand-picked each morning' },
  { emoji: '🚚', title: 'Daily delivery', text: 'To your doorstep, 7 days a week' },
  { emoji: '🌶️', title: 'Authentic Indian', text: 'Spices, dals & regional brands' },
  { emoji: '🧺', title: 'Free over $50', text: 'On every daily order' },
];

export default function Hero() {
  return (
    <Section>
      <Inner>
        <Copy>
          <Eyebrow>
            <span role="img" aria-label="India">🇮🇳</span> Bombay Trading Co
          </Eyebrow>
          <Title>
            Fresh Indian groceries,
            <br />
            <span>delivered daily.</span>
          </Title>
          <Lead>
            From farm-fresh vegetables to authentic spices and pantry staples —
            hand-picked every morning and brought to your door, every single day.
          </Lead>
          <Actions>
            <Primary type="button" onClick={scrollToMarkets}>
              Shop fresh groceries
            </Primary>
            <Ghost to="/about">See how delivery works →</Ghost>
          </Actions>
          <TrustRow>
            <Dot /> Delivering daily across the city · No subscription needed
          </TrustRow>
        </Copy>

        <Visual aria-hidden="true">
          <PhotoStack>
            <PhotoMain src={photoMain} alt="" loading="lazy" />
            <PhotoTop src={photoTop} alt="" loading="lazy" />
            <PhotoBottom src={photoBottom} alt="" loading="lazy" />
          </PhotoStack>
          <DeliveryBadge>
            <span role="img" aria-label="delivery">🚚</span>
            <div>
              <b>Delivered today</b>
              <small>Order by 6&nbsp;pm</small>
            </div>
          </DeliveryBadge>
        </Visual>
      </Inner>

      <Features>
        {features.map((f) => (
          <Feature key={f.title}>
            <FEmoji>{f.emoji}</FEmoji>
            <div>
              <FTitle>{f.title}</FTitle>
              <FText>{f.text}</FText>
            </div>
          </Feature>
        ))}
      </Features>
    </Section>
  );
}

/* ---------- animations ---------- */
const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-10px); }
`;

/* ---------- layout ---------- */
export const Section = styled.section`
  position: relative;
  overflow: hidden;
  padding: 48px 18px 28px;
  background:
    radial-gradient(1200px 500px at 85% -10%, rgba(1, 191, 113, 0.16), transparent 60%),
    radial-gradient(900px 500px at 0% 110%, rgba(255, 154, 60, 0.14), transparent 55%),
    linear-gradient(180deg, #fffdf7 0%, #f4fbf4 100%);
  border-bottom: 1px solid rgba(31, 45, 61, 0.06);

  @media screen and (min-width: 768px) {
    padding: 72px 24px 40px;
  }
`;

export const Inner = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr;
  text-align: center;
  align-items: center;
  gap: 28px;

  @media screen and (min-width: 900px) {
    grid-template-columns: 1.1fr 0.9fr;
    text-align: left;
    gap: 40px;
  }
`;

export const Copy = styled.div`
  min-width: 0;
`;

export const Eyebrow = styled.span`
  display: inline-block;
  background: rgba(1, 191, 113, 0.12);
  color: #0a8f57;
  font-weight: 700;
  font-size: 0.82rem;
  letter-spacing: 0.04em;
  padding: 7px 14px;
  border-radius: 999px;
  margin-bottom: 18px;
`;

export const Title = styled.h1`
  margin: 0 0 16px;
  color: #1f2d3d;
  font-size: clamp(2.1rem, 5.2vw, 3.6rem);
  line-height: 1.05;
  font-weight: 800;
  letter-spacing: -0.02em;

  span {
    background: linear-gradient(90deg, #01bf71, #ff9a3c);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

export const Lead = styled.p`
  margin: 0 auto 28px;
  color: #46586a;
  font-size: clamp(1rem, 1.6vw, 1.18rem);
  line-height: 1.6;
  max-width: 34em;

  @media screen and (min-width: 900px) {
    margin-left: 0;
    margin-right: 0;
  }
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: center;
  justify-content: center;

  @media screen and (min-width: 900px) {
    justify-content: flex-start;
  }
`;

export const Primary = styled.button`
  border: none;
  cursor: pointer;
  background: #01bf71;
  color: #fff;
  font-size: 1.02rem;
  font-weight: 700;
  padding: 15px 30px;
  border-radius: 999px;
  box-shadow: 0 10px 24px rgba(1, 191, 113, 0.35);
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  &:hover {
    background: #04a763;
    transform: translateY(-2px);
    box-shadow: 0 14px 30px rgba(1, 191, 113, 0.42);
  }
`;

export const Ghost = styled(LinkR)`
  color: #1f2d3d;
  font-weight: 700;
  font-size: 1rem;
  text-decoration: none;
  padding: 14px 18px;
  border-radius: 999px;
  transition: color 0.15s ease, background 0.15s ease;

  &:hover {
    color: #01bf71;
    background: rgba(1, 191, 113, 0.08);
  }
`;

export const TrustRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  margin-top: 26px;
  color: #6a7b8a;
  font-size: 0.92rem;

  @media screen and (min-width: 900px) {
    justify-content: flex-start;
  }
`;

export const Dot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #01bf71;
  box-shadow: 0 0 0 4px rgba(1, 191, 113, 0.2);
`;

/* ---------- visual ---------- */
export const Visual = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 240px;

  @media screen and (min-width: 480px) {
    min-height: 280px;
  }
  @media screen and (min-width: 900px) {
    min-height: 320px;
  }
`;

export const PhotoStack = styled.div`
  position: relative;
  width: min(400px, 92%);
  aspect-ratio: 1 / 1;
  animation: ${float} 7s ease-in-out infinite;
`;

export const PhotoMain = styled.img`
  position: absolute;
  top: 6%;
  left: 4%;
  width: 72%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 18px;
  border: 4px solid #fff;
  box-shadow: 0 24px 48px rgba(31, 45, 61, 0.18);
  transform: rotate(-3deg);
  background: #eef7ef;
`;

export const PhotoTop = styled.img`
  position: absolute;
  top: 0;
  right: 0;
  width: 44%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  border-radius: 16px;
  border: 4px solid #fff;
  box-shadow: 0 18px 36px rgba(31, 45, 61, 0.16);
  transform: rotate(4deg);
  background: #fdf3e4;
`;

export const PhotoBottom = styled.img`
  position: absolute;
  bottom: 4%;
  right: 12%;
  width: 40%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 16px;
  border: 4px solid #fff;
  box-shadow: 0 18px 36px rgba(31, 45, 61, 0.16);
  transform: rotate(-6deg);
  background: #eef2f7;
`;

export const DeliveryBadge = styled.div`
  position: absolute;
  right: 0;
  bottom: 2%;
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  padding: 12px 16px;
  border-radius: 16px;
  box-shadow: 0 16px 34px rgba(31, 45, 61, 0.16);

  span {
    font-size: 1.7rem;
  }
  b {
    display: block;
    color: #1f2d3d;
    font-size: 0.92rem;
  }
  small {
    color: #6a7b8a;
    font-size: 0.78rem;
  }

  @media screen and (min-width: 480px) {
    right: 4%;
    bottom: 8%;
  }
`;

/* ---------- feature strip ---------- */
export const Features = styled.div`
  max-width: 1180px;
  margin: 44px auto 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media screen and (min-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media screen and (min-width: 900px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

export const Feature = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(31, 45, 61, 0.07);
  border-radius: 16px;
  padding: 16px 18px;
  backdrop-filter: blur(4px);
`;

export const FEmoji = styled.div`
  flex: 0 0 auto;
  width: 46px;
  height: 46px;
  display: grid;
  place-items: center;
  font-size: 1.5rem;
  border-radius: 12px;
  background: rgba(1, 191, 113, 0.1);
`;

export const FTitle = styled.div`
  color: #1f2d3d;
  font-weight: 700;
  font-size: 0.98rem;
`;

export const FText = styled.div`
  color: #6a7b8a;
  font-size: 0.85rem;
  line-height: 1.35;
`;
